import { MindyViewContext } from '@quadminds/shared/mindy-context';
import { AxiosProgressEvent } from 'axios';

// Chat types
export interface ChatMessage {
    type: 'user' | 'assistant';
    content: string;
}

export interface ChatResponse {
    output: string;
}

export type AttachmentType = 'image' | 'chart';

export type ChartAttachmentType = 'LineChart' | 'BarChart' | 'PieChart' | 'RadarChart' | 'AreaChart';

export interface ImageAttachment {
    type: AttachmentType;
    url: string;
    alt: string;
}

export interface LineChartData {
    [key: string]: number | string; // The value of the data point, can be a number or string
}

export interface RadarChartData {
    subject: string | number;
    fullMark: number;
    [key: string]: number | string;
}

export interface PieChartData {
    name: string; // The name of the data point
    value: number; // The value of the data point
}

export type ChartData = LineChartData | PieChartData[] | RadarChartData;

type AxisKey = {
    show: boolean;
    key?: string;
};

export interface ChartAttachment {
    type: AttachmentType;
    chartType: ChartAttachmentType;
    data: ChartData[];
    options?: {
        xKey?: AxisKey; // x-axis key
        yKey?: AxisKey; // y-axis key
        showLegend?: boolean;
        pieShowAsPercentage?: boolean;
        barChartStacked?: boolean;
        [key: string]: unknown;
    };
}

export type Attachment = ImageAttachment | ChartAttachment;

export interface AgentAPIResponse {
    conversation_id: string;
    interaction_id: string;
    user_query: string;
    agent_response: string;
    context_texts: {
        text: string;
        filename: string;
        score: number;
    }[];
    attachments?: Attachment[];
}

// Conversation interaction types
export interface ConversationInteraction {
    _id: string;
    conversation_id: string;
    organization_id: string;
    user_id: string;
    agent_id: string;
    user_message: string;
    agent_response: string;
    context: Record<string, unknown>[];
    tools_used: string[];
    metadata: Record<string, unknown>;
    usage_metadata: Record<string, unknown>;
    rating: string;
    feedback_comment: string;
    rated_at: string | null;
    attachments?: Attachment[];
}

// Alias for backward compatibility
export type Interaction = ConversationInteraction;

// Conversation types
export interface Conversation {
    _id: string;
    user_id: string;
    title: string;
    status: string;
    interactions?: ConversationInteraction[];
    metadata?: Record<string, unknown>;
    usage_metadata?: Record<string, unknown>;
    created_at?: string;
    updated_at?: string;
}

// Document types
export interface Document {
    id?: string;
    _id?: string;
    name: string;
    title?: string | null;
    filename?: string | null;
    status: string;
    active: boolean;
    parent_id?: string | null;
    organization_id: string;
    owner_id: string;
    uploaded_by?:
        | {
              userId: string;
              fullName: string;
              photo: string;
          }
        | string
        | null;
    summary?: string | null;
    tags?: string[];
    size?: number | null;
    chunk_count?: number | null;
    page_count?: number | null;
    processing_time_ms?: number | null;
    processing_metadata?: {
        processing_status?: string;
    };
    content_metadata?: {
        page_count?: number;
        word_count?: number;
        character_count?: number;
        language?: string;
        entities?: Array<{
            type: string;
            value: string;
            count: number;
        }>;
    };
    created_at?: string;
    updated_at?: string | null;
    error?: string | null;
    document_permissions?: string[];
}

export interface Folder {
    id: string;
    name: string;
    type?: string;
    parent_id?: string;
    organization_id: string;
    owner_id?: string;
    uploaded_by?: {
        userId: string;
        fullName: string;
    };
    child_count?: number;
    created_at?: string;
    updated_at?: string;
}

export interface Paginated<TData, TFolder = Folder> {
    data: TData[];
    folders?: TFolder[];
    meta: {
        total_folders: number;
        total_documents: number;
        path: { id: string; name: string }[];
    };
    pagination: {
        page: number;
        page_size: number;
        total_documents: number;
        total_pages: number;
    };
}

// File types
export interface FileInput {
    id: string;
    filename: string;
    conversation_id?: string;
    status: string;
}

export interface FileOutput {
    id: string;
    filename: string;
    status: string;
    processing_status?: string;
    organization_id: string;
    message?: string;
}

// Message types
export interface MessageOutput {
    query: string;
    agent_id?: string;
    impersonated_user_email?: string;
    original_user_id?: string;
    mindy_version: string;
    user_id?: string;
    is_impersonated: boolean;
    conversation_id?: string;
    has_files?: boolean;
    user_alias?: string;
    source?: string;
    /** Contexto de pantalla del SaaS (QS-19310). Sólo se manda con source === 'embedded'. */
    query_context?: MindyViewContext;
    knowledge_destination?: string;
} // YPF types
export interface YpfImportResult {
    id?: string;
    file_name: string;
    filename?: string;
    file_size_mb: number;
    status?: string;
    imported_rows: {
        total: number;
        new_records: number;
        updated_records: number;
    };
    error_summary: {
        total_errors: number;
        total_error_rows: number;
        error_row_numbers: number[];
        details: string[];
    };
    errors?: string[];
}

// Upload progress event with name
export interface UploadProgressEvent extends AxiosProgressEvent {
    name: string;
}

// Google Auth types
export interface GoogleAuthResponse {
    authorization_url: string;
    state: string;
}

export interface GoogleCallbackResponse {
    access_token: string;
}

// Rating types
export type RatingType = 'positive' | 'negative';

export interface RateResponsePayload {
    rating: RatingType;
    feedback_comment: string;
    agent_version: string;
}

// Folder operation types
export interface CreateFolderPayload {
    name: string;
    parent_id?: string;
}

export interface UpdateFolderPayload {
    name: string;
    parent_id?: string;
}

export interface MoveDocumentPayload {
    organization_id: string;
    destination_folder_id?: string;
}

export interface MoveDocumentResponse {
    success: boolean;
    message?: string;
}

// Import types
export interface ImportDocumentFromGooglePayload {
    file_id: string;
    parent_id: string | null;
    file_name: string;
    external_id: string;
}

export interface ImportDocumentFromUriPayload {
    url: string;
    auto_detect_strategy: boolean;
    external_id: string;
    parent_id: string | null;
}

// Statistics types
export interface FolderStatistics {
    total_documents: number;
    organization_id: string;
    folder_id: string;
    by_status: Record<string, number>;
    by_active: Record<string, number>;
    by_processing_status: Record<string, number>;
}

// Signed URL types
export interface SignedUrlResponse {
    signed_url: string;
    expires_at: string;
    content_type: string;
}

export interface PermissionToAdd {
    folder_id: string;
    user?: {
        id: string;
        name: string;
        email: string;
        photo?: string | null;
    } | null;
    permissions: string[];
}

export interface SetPermissionRequest {
    permissions_to_add: PermissionToAdd[];
    permission_ids_to_delete: string[];
    document_id: string;
    generalPermission?: string[];
}

export interface DocumentUserPermission {
    id?: string;
    organization_id?: string | null;
    permissions: string[];
    roles?: { id: string; name: string }[];
    user_email?: string | null;
    user_id?: string | null;
    user_name?: string | null;
    user_photo?: string | null;
}
// Organization user bulk operation types
export interface BulkUserEntry {
    user_id: string;
    email: string;
}

export interface BulkUsersResponse {
    added: number;
    failed: number;
}

export type FeedbackType = 'question' | 'release_notes';

export interface GeneralFeedback {
    id?: string;
    text: string;
    type: FeedbackType;
    user_id: string;
    user_name: string;
    created_at?: string;
    is_impersonated: boolean;
}

export interface ReleaseNoteItem {
    version: string;
    created_at: string;
    features: string[];
}
